import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FaSearch, FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { Modal, Button, Form } from 'react-bootstrap';

function CategoryList() {
    const [categories, setCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(100);

    // For modal state
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false); // New modal for deletion confirmation
    const [isEditing, setIsEditing] = useState(false); // Track if we're editing
    const [categoryId, setCategoryId] = useState(null); // Store the category ID for editing
    const [categoryName, setCategoryName] = useState('');
    const [categoryPhoto, setCategoryPhoto] = useState(null);
    const [imagePreview, setImagePreview] = useState(null); // For previewing the selected image

    const fetchCategories = async () => {
        const token = localStorage.getItem('authToken');
        if (token) {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/getcategories`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch categories');
                }

                const result = await response.json();
                setCategories(result.data || []);
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    const filteredCategories = categories.filter((category) =>
        category.category && category.category.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentCategories = filteredCategories.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Handle category form submission (Add/Edit)
    const handleAddOrEditCategory = async () => {
        const token = localStorage.getItem('authToken');
        const formData = new FormData();
        formData.append('category_name', categoryName);
        formData.append('category_photo', categoryPhoto);

        try {
            const method = isEditing ? 'PUT' : 'POST';
            const url = isEditing
                ? `${process.env.REACT_APP_API_URL}/updatecategory/${categoryId}`
                : `${process.env.REACT_APP_API_URL}/addcategory`;

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error(isEditing ? 'Failed to update category' : 'Failed to add category');
            }

            const result = await response.json();
            if (isEditing) {
                setCategories(categories.map((cat) => (cat.id === categoryId ? result.data : cat))); // Update edited category
            } else {
                setCategories([...categories, result.data]); // Add new category
            }
            fetchCategories();
            setShowModal(false); // Close the modal
            resetForm(); // Reset form after submission
        } catch (error) {
            console.error('Error adding/updating category:', error);
        }
    };

    // Handle the Edit button click
    const handleEditClick = (category) => {
        setCategoryId(category.id);
        setCategoryName(category.category);
        setCategoryPhoto(null); // Optionally, keep the current photo for editing
        setImagePreview(`${category.categoryIcon}`); // Show existing image as preview
        setIsEditing(true); // Set editing mode
        setShowModal(true); // Show the modal
    };

    // Handle category deletion
    const handleDeleteClick = (categoryId) => {
        setCategoryId(categoryId); // Set the category ID to be deleted
        setShowDeleteModal(true); // Show the confirmation modal
    };

    const handleDeleteCategory = async () => {
        const token = localStorage.getItem('authToken');
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/deletecategory/${categoryId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to delete category');
            }
            fetchCategories();
            setCategories(categories.filter((category) => category.id !== categoryId)); // Remove deleted category from the list
            setShowDeleteModal(false); // Close the delete confirmation modal
        } catch (error) {
            console.error('Error deleting category:', error);
        }
    };

    // Handle image change (for preview)
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCategoryPhoto(file);
            setImagePreview(URL.createObjectURL(file)); // Create preview URL for the image
        }
    };

    // Reset form fields
    const resetForm = () => {
        setCategoryName('');
        setCategoryPhoto(null);
        setImagePreview(null);
        setCategoryId(null);
        setIsEditing(false); // Reset editing state
    };

    // Handle modal close (reset form)
    const handleModalClose = () => {
        resetForm(); // Reset the form when the modal is closed
        setShowModal(false);
    };

    return (
        <div className="container" style={{ marginTop: '80px' }}>
            <h2 className="text-primary">Category List</h2>

            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="input-group me-3" style={{ maxWidth: '300px' }}>
                    <span className="input-group-text bg-primary text-white">
                        <FaSearch />
                    </span>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search categories..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button className="btn btn-success" onClick={() => setShowModal(true)}>
                    <FaPlus /> Add Category
                </button>
            </div>

            {/* Table View */}
            <table className="table table-bordered table-striped">
                <thead className="table-primary">
                    <tr>
                        <th>ID</th>
                        <th>Category</th>
                        <th>Image</th>
                        <th>Created At</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {currentCategories.map((category) => (
                        <tr key={category.id}>
                            <td>{category.id}</td>
                            <td>{category.category}</td>
                            <td>
                                <img
                                    src={`${category.categoryIcon}`}
                                    alt={category.category}
                                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                />
                            </td>
                            <td>{new Date(category.createdAt).toLocaleDateString()}</td>
                            <td>
                                <button className="btn btn-sm btn-outline-success me-2" onClick={() => handleEditClick(category)}>
                                    <FaEdit /> Edit
                                </button>
                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteClick(category.id)}>
                                    <FaTrash /> Delete
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Pagination */}
            <nav>
                <ul className="pagination justify-content-center">
                    {[...Array(totalPages)].map((_, index) => (
                        <li key={index} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => handlePageChange(index + 1)}>
                                {index + 1}
                            </button>
                        </li>
                    ))}
                </ul>
            </nav>

            {/* Modal for Adding/Editing Category */}
            <Modal show={showModal} onHide={handleModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? 'Edit Category' : 'Add New Category'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group controlId="categoryName">
                            <Form.Label>Category Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter category name"
                                value={categoryName}
                                onChange={(e) => setCategoryName(e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group controlId="categoryPhoto" className="mt-3">
                            <Form.Label>Category Photo</Form.Label>
                            <Form.Control type="file" onChange={handleImageChange} />
                            {imagePreview && <img src={imagePreview} alt="Preview" className="mt-3" style={{ width: '100px' }} />}
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleModalClose}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleAddOrEditCategory}>
                        {isEditing ? 'Update Category' : 'Add Category'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>Are you sure you want to delete this category?</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDeleteCategory}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default CategoryList;
